'use client';

import React from 'react';
import { Box, Editable, EditableInput, EditablePreview, useEditableState } from '@chakra-ui/react';

function EditablePreviewOpenOnClick(props: React.ComponentProps<typeof EditablePreview>) {
    const { onEdit } = useEditableState();
    const { onClick, ...rest } = props;
    return (
        <EditablePreview
            {...rest}
            data-line-item-editable-preview
            cursor="pointer"
            onClick={(e) => {
                onEdit?.();
                onClick?.(e);
            }}
        />
    );
}

export type LineItemGridLandingEditableProps = {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    isReadOnly?: boolean;
    /** Spread onto Chakra `Editable` (e.g. flex, minW, textAlign, w). */
    editableRootProps?: Omit<React.ComponentProps<typeof Editable>, 'value' | 'onChange' | 'isDisabled' | 'children'>;
    editableInputProps?: React.ComponentProps<typeof EditableInput>;
    editablePreviewProps?: React.ComponentProps<typeof EditablePreview>;
};

/**
 * Chakra Editable with a focusable landing shell so keyboard grid nav can land on the cell (Excel-like)
 * without opening the input; first typed character replaces the value via parent handler.
 */
export function LineItemGridLandingEditable({
    value,
    onChange,
    placeholder,
    isReadOnly,
    editableRootProps,
    editableInputProps,
    editablePreviewProps,
}: LineItemGridLandingEditableProps) {
    const [draftValue, setDraftValue] = React.useState(value);

    React.useEffect(() => {
        setDraftValue(value);
    }, [value]);

    const inputProps = editableInputProps ?? {};
    const { onBlur: inputOnBlur, ...restInputProps } = inputProps;

    return (
        <Box
            data-line-item-grid-landing
            tabIndex={isReadOnly ? undefined : 0}
            w="100%"
            sx={{
                borderRadius: 'sm',
                _focusVisible: {
                    outline: '2px solid',
                    outlineColor: 'blue.500',
                    outlineOffset: '1px',
                },
            }}
        >
            <Editable
                value={draftValue}
                onChange={setDraftValue}
                onSubmit={(nextValue) => {
                    setDraftValue(nextValue);
                    onChange(nextValue);
                }}
                onCancel={() => {
                    setDraftValue(value);
                }}
                submitOnBlur={true}
                placeholder={placeholder}
                isDisabled={!!isReadOnly}
                isPreviewFocusable={false}
                selectAllOnFocus={false}
                w="100%"
                {...editableRootProps}
            >
                <EditablePreviewOpenOnClick {...(editablePreviewProps ?? {})} />
                <EditableInput
                    {...restInputProps}
                    onBlur={(e) => {
                        // `submitOnBlur={true}` on Editable handles committing the draft value via onSubmit.
                        // Clean up the legacy commit-on-blur flag (set by the grid keyboard-nav hook) so
                        // it doesn't leak into the DOM across renders.
                        if ((e.currentTarget as HTMLInputElement).dataset.commitOnBlur === '1') {
                            delete (e.currentTarget as HTMLInputElement).dataset.commitOnBlur;
                        }
                        inputOnBlur?.(e);
                    }}
                />
            </Editable>
        </Box>
    );
}
